import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { Save, Copy, Eye, EyeOff, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import WifiWhatsappSettings from "./WifiWhatsappSettings";

const SECURITY_TYPES = ["WPA2", "WPA3", "WPA", "WEP", "Aberta"];

// slug: only lowercase letters, digits, hyphens, max 60 chars
function sanitizeSlug(v) {
  return v.toLowerCase().replace(/[^a-z0-9-]/g, "").substring(0, 60);
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <Label className="text-sm font-medium">{label}</Label>
      {children}
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

export default function SocialConnectSettings({ settings, onSave, saving }) {
  const [form, setForm] = useState(settings || {});
  const [showPwd, setShowPwd] = useState(false);
  const qrCanvasRef = useRef(null);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const connectUrl = form.portal_slug
    ? `${window.location.origin}/connect/${form.portal_slug}`
    : null;

  const copyUrl = () => {
    if (!connectUrl) return;
    navigator.clipboard.writeText(connectUrl);
    toast.success("URL copiada!");
  };

  const downloadQR = () => {
    const canvas = qrCanvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-wifi-${form.portal_slug || "portal"}.png`;
    a.click();
    toast.success("QR Code guardado!");
  };

  const printQR = () => {
    const canvas = qrCanvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>QR Wi-Fi — ${form.portal_slug || "portal"}</title>
      <style>
        body { margin: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; font-family: sans-serif; }
        img { width: 300px; height: 300px; }
        p { margin: 12px 0 4px; font-size: 14px; color: #444; }
        h2 { margin: 0 0 8px; font-size: 20px; }
        @media print { button { display: none; } }
      </style></head>
      <body>
        <h2>Wi-Fi Grátis</h2>
        <p>Aponta a câmera para ligar</p>
        <img src="${url}" />
        <p style="font-size:12px;color:#888">${connectUrl}</p>
        <button onclick="window.print()" style="margin-top:20px;padding:10px 24px;font-size:14px;cursor:pointer">Imprimir</button>
      </body></html>
    `);
    win.document.close();
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-6">

      {/* Enable toggle */}
      <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
        <div>
          <p className="font-medium">Social WiFi Connect ativo</p>
          <p className="text-sm text-gray-500">
            Clientes preenchem um formulário e recebem a senha do Wi-Fi — sem hardware adicional
          </p>
        </div>
        <Switch checked={!!form.social_connect_enabled}
          onCheckedChange={(v) => set("social_connect_enabled", v)} />
      </div>

      {/* QR code section — only visible when enabled + slug exists */}
      {form.social_connect_enabled && connectUrl && (
        <div className="p-4 border rounded-lg space-y-3">
          <p className="font-semibold text-sm">QR Code do portal</p>
          {/* Hidden high-res canvas for download/print */}
          <div ref={qrCanvasRef} style={{ display: "none" }}>
            <QRCodeCanvas value={connectUrl} size={1024} />
          </div>
          <div className="flex gap-6 items-start flex-wrap">
            <QRCodeSVG value={connectUrl} size={128} />
            <div className="flex-1 min-w-0 space-y-2">
              <p className="text-xs text-gray-500 break-all">{connectUrl}</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={copyUrl}>
                  <Copy className="h-3 w-3 mr-1" /> Copiar URL
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={downloadQR}>
                  <Download className="h-3 w-3 mr-1" /> Baixar QR
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={printQR}>
                  <Printer className="h-3 w-3 mr-1" /> Imprimir
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {form.social_connect_enabled && !form.portal_slug && (
        <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 p-3 rounded-lg">
          Define um slug do portal abaixo para gerar o QR code.
        </p>
      )}

      {/* Portal slug */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">Identificação do portal</h3>
        <Field label="Slug do portal" hint="Apenas letras minúsculas, números e hífens. Ex: meu-restaurante">
          <Input value={form.portal_slug || ""}
            onChange={(e) => set("portal_slug", sanitizeSlug(e.target.value))}
            placeholder="meu-restaurante" />
        </Field>
      </section>

      {/* WiFi credentials */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">Credenciais Wi-Fi</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Nome da rede (SSID)" hint="Aparece na lista de Wi-Fi do cliente">
            <Input value={form.wifi_ssid || ""} onChange={(e) => set("wifi_ssid", e.target.value)}
              placeholder="MinhaRede_WiFi" />
          </Field>
          <Field label="Senha do Wi-Fi">
            <div className="relative">
              <Input type={showPwd ? "text" : "password"} value={form.wifi_password || ""}
                onChange={(e) => set("wifi_password", e.target.value)} placeholder="senha123" />
              <button type="button" onClick={() => setShowPwd((s) => !s)}
                className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600">
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>
          <Field label="Segurança">
            <Select value={form.wifi_security_type || "WPA2"}
              onValueChange={(v) => set("wifi_security_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SECURITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </section>

      {/* Page content */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">Conteúdo da página</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Título"><Input value={form.connect_page_title || ""}
            onChange={(e) => set("connect_page_title", e.target.value)}
            placeholder="Acesse o Wi-Fi Grátis!" /></Field>
          <Field label="Subtítulo"><Input value={form.connect_page_subtitle || ""}
            onChange={(e) => set("connect_page_subtitle", e.target.value)}
            placeholder="Cadastre-se para ter acesso gratuito" /></Field>
          <Field label="URL do logo"><Input value={form.logo_url || ""}
            onChange={(e) => set("logo_url", e.target.value)} placeholder="https://..." /></Field>
          <Field label="Cor principal">
            <div className="flex gap-2 items-center">
              <input type="color" value={form.brand_primary_color || "#FF6B35"}
                onChange={(e) => set("brand_primary_color", e.target.value)}
                className="h-9 w-12 rounded border cursor-pointer" />
              <Input value={form.brand_primary_color || ""}
                onChange={(e) => set("brand_primary_color", e.target.value)} className="flex-1" />
            </div>
          </Field>
        </div>
      </section>

      {/* Social links */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">Links sociais</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Instagram"><Input value={form.instagram_url || ""}
            onChange={(e) => set("instagram_url", e.target.value)} placeholder="https://instagram.com/..." /></Field>
          <Field label="WhatsApp (número)"><Input value={form.whatsapp_number || ""}
            onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="+351 912 345 678" /></Field>
          <Field label="Google Review"><Input value={form.google_review_url || ""}
            onChange={(e) => set("google_review_url", e.target.value)} placeholder="https://g.page/..." /></Field>
          <Field label="TikTok"><Input value={form.tiktok_url || ""}
            onChange={(e) => set("tiktok_url", e.target.value)} placeholder="https://www.tiktok.com/@..." /></Field>
        </div>
      </section>

      {/* Post-registration */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">Pós-registo</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Cupão (opcional)"><Input value={form.coupon_code || ""}
            onChange={(e) => set("coupon_code", e.target.value)} placeholder="WIFI10" /></Field>
          <Field label="Mensagem de sucesso"><Input value={form.success_message || ""}
            onChange={(e) => set("success_message", e.target.value)} placeholder="Wi-Fi liberado! Obrigado." /></Field>
        </div>
      </section>

      {/* GDPR */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">GDPR</h3>
        <Field label="Texto de consentimento">
          <Textarea value={form.marketing_consent_text || ""}
            onChange={(e) => set("marketing_consent_text", e.target.value)} rows={2}
            placeholder="Autorizo o uso dos meus dados para receber comunicações promocionais..." />
        </Field>
        <Field label="URL Política de Privacidade">
          <Input value={form.privacy_policy_url || ""}
            onChange={(e) => set("privacy_policy_url", e.target.value)} placeholder="https://..." />
        </Field>
      </section>

      {/* Special offer */}
      <section className="space-y-4">
        <h3 className="font-semibold text-gray-700">Oferta Especial</h3>
        <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
          <div>
            <p className="font-medium">Ativar oferta especial</p>
            <p className="text-sm text-gray-500">
              Mostra um card de oferta na página pública antes do registo
            </p>
          </div>
          <Switch
            checked={!!form.special_offer_enabled}
            onCheckedChange={(v) => set("special_offer_enabled", v)}
          />
        </div>
        {form.special_offer_enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Título da oferta">
              <Input value={form.special_offer_title || ""}
                onChange={(e) => set("special_offer_title", e.target.value)}
                placeholder="10% DESCONTO" />
            </Field>
            <Field label="Descrição">
              <Input value={form.special_offer_description || ""}
                onChange={(e) => set("special_offer_description", e.target.value)}
                placeholder="Na próxima visita" />
            </Field>
            <Field label="Código do cupão" hint="Revelado ao cliente após o registo">
              <Input value={form.special_offer_coupon || ""}
                onChange={(e) => set("special_offer_coupon", e.target.value)}
                placeholder="WIFI10" />
            </Field>
            <Field label="Validade">
              <Input value={form.special_offer_expiration || ""}
                onChange={(e) => set("special_offer_expiration", e.target.value)}
                placeholder="31/12/2025" />
            </Field>
            <Field label="Cor da oferta">
              <div className="flex gap-2 items-center">
                <input type="color" value={form.special_offer_color || "#E53E3E"}
                  onChange={(e) => set("special_offer_color", e.target.value)}
                  className="h-9 w-12 rounded border cursor-pointer" />
                <Input value={form.special_offer_color || ""}
                  onChange={(e) => set("special_offer_color", e.target.value)}
                  className="flex-1" />
              </div>
            </Field>
          </div>
        )}
      </section>

      <WifiWhatsappSettings form={form} set={set} />

      <Button type="submit" disabled={saving}>
        <Save className="h-4 w-4 mr-2" />{saving ? "A guardar..." : "Guardar"}
      </Button>
    </form>
  );
}
